import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  TextField,
  InputAdornment,
  Box,
  Typography,
  IconButton,
  SvgIcon,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { getIconComponent } from "@/Utils/getIconComponent";
import FormLabel from "./FormLabel";
import {
  LuSearch,
  VisibilityOffOutlinedIcon,
  VisibilityOutlinedIcon,
  CloseIcon,
  BsCheckAll,
} from "@/Components/Icons/Icons";
import { useWatch } from "react-hook-form";
import { useUser } from "@/Context/UserContext";

// Styled TextField to remove number input spinners
const NoSpinnerTextField = styled(TextField)({
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    {
      "-webkit-appearance": "none",
      margin: 0,
    },
  "& input[type=number]": {
    "-moz-appearance": "textfield",
  },
});

const InputTextField = ({
  field,
  name,
  dataType,
  label,
  placeholder,
  value,
  onChange,
  icon,
  iconOnClick,
  type = "",
  error = false,
  helperText = "",
  inputMode,
  width,
  height = 40,
  disabled,
  startIconName,
  endIconName,
  iconColor,
  gap,
  required = false,
  afterText = "",
  sx,
  options,
  freeSolo = false,
  labelKey,
  valueKey,
  onSelectOption,
  loading = false,
  isOTPField = false, // add this line
  autofillTargets,
  updateAutofillTargets,
  clearDependentLinkedFields,
  control,
  editable,
  defaultValue,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const isPassword = type === "password";
  const inputType = isPassword ? (showPassword ? "text" : "password") : type;
  const isEmpty = value === "";
  const [open, setOpen] = useState(false);
  const showError = error || (required && isTouched && isEmpty);
  const [fetchError, setFetchError] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const watchedValue = control ? useWatch({ control, name }) : value;
  const { setValueGlobal } = useUser();
  const [visibleCount, setVisibleCount] = useState(50);
  const [lazyOptions, setLazyOptions] = useState([]);
  const listboxRef = useRef(null);
  const handlePhoneInput = (value) => {
    if (!value) return "";
    // Remove non-numeric characters
    let onlyNumbers = value.replace(/\D/g, "");
    // Strict limit: max 10 digits
    return onlyNumbers.slice(0, 10); // 10 digits mela allow pannathu
  };
  // const watchedValue = useWatch({control, name });

  const handleChange = (e) => {
    setIsTouched(true);
    const { value, name } = e.target;

    let newValue = value;

    if (type === "phone") {
      newValue = handlePhoneInput(newValue);
    }

    if (dataType === "int") {
      // allow only digits
      if (!/^\d*$/.test(newValue)) return;
    }

    if (dataType === "float") {
      const valStr = String(newValue);

      // Allow user to type partial floats like ".", "1150.", "1150.5"
      if (!/^\d*\.?\d{0,100}$/.test(valStr)) return;

      // Don't block when user starts with "."
      if (valStr === ".") {
        newValue = "0.";
      }

      // Don’t parse to float while typing (keep as string)
    }

    if (label?.toLowerCase() === "username") {
      const regex = /^[a-zA-Z0-9_]+ [a-zA-Z0-9_]+$/;
      if (/\s{2,}/.test(newValue)) return;
      if (newValue.startsWith(" ")) return;
      if (newValue.endsWith("  ")) return;
    }

    if (type === "password") {
      newValue = newValue.replace(/\s/g, "");
    }

    if (dataType === "int" && newValue !== "") {
      newValue = parseInt(newValue, 10);
    }

    onChange({ ...e, target: { ...e.target, name, value: newValue } });
  };

  const handleBlur = (e) => {
    setIsTouched(true);

    if (dataType === "float" && e.target.value !== "") {
      const num = parseFloat(e.target.value);
      if (!isNaN(num)) {
        onChange({ ...e, target: { ...e.target, value: num } });
      }
    }
  };

  const renderIcon = (iconName) => {
    iconName
      ? getIconComponent(iconName, {
          color: iconColor || "var(--gray-400)",
          size: 50,
          fontWeight: "bold",
        })
      : null;
  };

  const normalizedOptions = useMemo(() => {
    if (!Array.isArray(options)) return [];
    return options.map((opt) => {
      // normalize shape to { id, label, value, raw }
      const id = opt?.id ?? (opt?.value && opt.value.id) ?? opt?.value ?? opt;
      const value = opt?.value ?? opt?.id ?? id;
      const label =
        opt?.label ?? opt?.name ?? (typeof opt === "string" ? opt : "");
      const description = opt?.description ?? opt?.raw?.description ?? "";
      const aliseName = opt?.aliseName ?? opt?.raw?.aliseName ?? "";
      return { id, value, label, raw: opt, description, aliseName };
    });
  }, [options]);

  const isFiltering = typeof inputValue === "string" ? inputValue : "" ?? inputValue?.trim().length >= 2;
  const actualValue = value ?? watchedValue;

  useEffect(() => {
    // Clear UI when value is cleared by parent
    if (
      !isFiltering &&
      (actualValue === null || actualValue === undefined || actualValue === "")
    ) {
      if (selectedOption !== null) {
        setSelectedOption(null);
        setInputValue(""); // ✅ clear visible text too
      }
      return;
    }

    if (!normalizedOptions.length) return;

    const match =
      normalizedOptions.find((opt) => {
        const optId = opt.id ?? opt.value;
        if (typeof actualValue === "object" && actualValue !== null) {
          return (
            (optId || optId?.id) === actualValue.id ||
            optId === actualValue.value ||
            optId === actualValue?.name
          );
        }
        return optId === actualValue;
      }) || null;
    const same =
      (match === null && selectedOption === null) ||
      (match !== null &&
        selectedOption !== null &&
        match.id === selectedOption.id);
    if (!same) {
      setSelectedOption(match || null || {});
      // setInputValue(match); // ✅ keep text consistent
    }
    
  }, [actualValue, normalizedOptions, selectedOption, name]);

  const filteredOptions = useMemo(() => {
    if (!inputValue?.trim()) return [];

    const search = inputValue.toLowerCase();

    return normalizedOptions.filter(
      (opt) =>
        opt.label?.toLowerCase().includes(search) ||
        opt.value?.toString().toLowerCase().includes(search)
    );
  }, [inputValue, normalizedOptions]);

  const autocompleteOptions = useMemo(() => {
    if (inputValue?.trim()) {
      return filteredOptions; // 🔍 typing mode
    }
    return lazyOptions; // 📜 scroll mode
  }, [inputValue, filteredOptions, lazyOptions]);

  useEffect(() => {
    if (!normalizedOptions.length) return;

    setLazyOptions(normalizedOptions.slice(0, visibleCount));
  }, [normalizedOptions, visibleCount]);

  const jsonValue = typeof value === "object" ? value?.name : value;

  const renderWithAutocomplete = () => {
    const handleSearchClick = (e) => {
      e.stopPropagation();
      if (!disabled) setOpen(true);
    };

    return (
      <Autocomplete
        freeSolo={true}
        selectOnFocus={false}
        disablePortal
        disabled={disabled}
        options={selectedOption ? [selectedOption] : autocompleteOptions}
        value={selectedOption}
        open={open}
        inputValue={inputValue}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        clearOnBlur={false}
        PopperProps={{
          disablePortal: false,
          modifiers: [
            {
              name: "zIndex",
              enabled: true,
              phase: "write",
              fn: ({ state }) => {
                state.styles.popper.zIndex = 2000;
                state.styles.popper.maxHeight = "300px";
                state.styles.popper.overflowY = "auto";
              },
            },
          ],
        }}
        ListboxProps={{
          ref: listboxRef,
          onScroll: (e) => {
            const list = e.target;

            if (list.scrollTop + list.clientHeight >= list.scrollHeight - 10) {
              // user reached bottom → load next 50
              setVisibleCount((prev) => prev + 50);
            }
          },
          style: {
            maxHeight: 250,
            overflowY: "auto",
            padding: 0,
            backgroundColor: "#fff",
            border: "1px solid var(--gray-200)",
            borderRadius: 6,
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
          },
        }}
        isOptionEqualToValue={(option, value) =>
          option?.value === value?.value ||
          option?.label === value?.label ||
          option?.name === value?.name
        }
        getOptionLabel={(option) => {
          if (!option || typeof option !== "object") return "";

          if (!option) return "";

          if (typeof option?.label === "string" && option.label.trim() !== "") {
            return option.label;
          }
          if (option?.name?.name) {
            return option.name.name;
          }
          if (option?.value?.name) {
            return option.value?.name;
          }
          return String(option?.name ?? option?.value ?? "");
        }}
        loading={loading}
        onInputChange={(event, newInputValue) => {
          if (field?.filterEndPoint) {
            setValueGlobal(field.name + "__search", newInputValue);
          }
          const match = normalizedOptions.find(
            (opt) => opt.label?.toLowerCase() === newInputValue.toLowerCase()
          );

          if (match) {
            setSelectedOption(match);
          } else {
            setSelectedOption(null);
          }

          setInputValue(newInputValue);

          if (newInputValue.trim() === "") {
            setVisibleCount(50);
          }
        }}
        onChange={(e, newValue) => {
          // Close dropdown after selecting an option
          //  if (field?.filterEndPoint) {
          // }
          setOpen(false);

          if (!newValue) {
            onChange({ ...e, target: { ...e.target, value: "" } });
            if (onSelectOption) onSelectOption(null);
            if (field?.autofillTargets) {
              updateAutofillTargets(null, field, true);
            }
            return;
          }

          const finalVal =
            typeof newValue === "string" ? newValue : (newValue?.value ?? null);

          onChange({
            ...e,
            target: {
              ...e.target,
              value: finalVal,
              optionData: newValue,
            },
          });

          if (onSelectOption) onSelectOption(newValue?.raw || newValue);

          // 🪄 Autofill targets when option selectedimportJobFillingMode
          if (field?.autofillTargets) {
            updateAutofillTargets(newValue, field, true);
          }
        }}
        renderOption={(props, option) => {
          const { key, ...restProps } = props; // remove key from props

          // extract only primitive (string/number/boolean) values
          const singleValueEntries = Object.entries(option || {})
            .filter(
              ([key, value]) =>
                key !== "label" &&
                key !== "name" &&
                key !== "id" &&
                (typeof value === "string" ||
                  typeof value === "number" ||
                  typeof value === "boolean")
            )
            .slice(0, 3); // show only first 3 primitive values

          return (
            <li {...restProps}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  px: 1,
                  py: 0.5,
                }}
              >
                {/* ✅ Always show label or name first */}
                <Typography sx={{ fontWeight: 600, color: "text.primary" }}>
                  {option?.label || option?.name || "Unnamed"}
                </Typography>

                {/* ✅ Show only primitive value fields */}
                {singleValueEntries.map(([key, value], idx) => (
                  <Typography
                    key={`${option?.id || option?.value || option?.label}_${key}_${idx}`}
                    sx={{
                      fontSize: 13,
                      color: "text.secondary",
                      textTransform: "capitalize",
                    }}
                  >
                    {`${key} : ${value}`}
                  </Typography>
                ))}
              </Box>
            </li>
          );
        }}
        renderInput={(params) => (
          <NoSpinnerTextField
            name={name}
            {...params}
            disabled={disabled}
            size="small"
            placeholder={placeholder}
            onBlur={handleBlur}
            error={showError || Boolean(fetchError)}
            helperText={
              fetchError
                ? fetchError
                : showError
                  ? helperText || `${label} is required`
                  : ""
            }
            InputProps={{
              ...params.InputProps,
              // readOnly: true,
              startAdornment: startIconName && (
                <InputAdornment position="start">
                  {typeof startIconName === "string"
                    ? renderIcon(startIconName)
                    : React.createElement(startIconName, {
                        color: iconColor || "var(--gray-400)",
                        size: 20,
                        fontWeight: "bold",
                      })}
                </InputAdornment>
              ),

              endAdornment: (
                <>
                  {loading ? (
                    <InputAdornment position="end">
                      <CircularProgress
                        size={18}
                        sx={{ color: "var(--brand-)" }}
                      />
                    </InputAdornment>
                  ) : (
                    <InputAdornment position="end">
                      {value &&
                      value !== null &&
                      value !== undefined &&
                      value !== "" &&
                      !(
                        typeof value === "object" &&
                        (value?.id === null ||
                          value?.id === undefined ||
                          value?.name === "")
                      ) ? (
                        <CloseIcon
                          size={20}
                          color={iconColor || "var(--gray-400)"}
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedOption({});
                            setInputValue("");
                            onChange({
                              ...e,
                              target: { ...e.target, value: "" },
                            });

                            if (autofillTargets && updateAutofillTargets) {
                              updateAutofillTargets(
                                null,
                                { name, autofillTargets },
                                true
                              );
                            }

                            if (onSelectOption) onSelectOption(null, true);
                            clearDependentLinkedFields(name);
                          }}
                        />
                      ) : (
                        <Box
                          onClick={handleSearchClick}
                          sx={{
                            cursor: disabled ? "not-allowed" : "pointer",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <LuSearch
                            color={iconColor || "var(--gray-400)"}
                            size={20}
                          />
                        </Box>
                      )}
                    </InputAdornment>
                  )}
                </>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "16px",
                height: height,
                backgroundColor: disabled ? "var(--gray-50)" : "var(--gray-0)",
                cursor: disabled ? "not-allowed" : "text",
                paddingRight: "6px !important",
                paddingTop: "2px",
                paddingBottom: "2px",
                "& fieldset": {
                  border: showError
                    ? "1px solid var(--red-900) !important"
                    : "1px solid var(--gray-300)",
                  borderRadius: 2,
                },
                "&:hover fieldset": {
                  borderColor: showError ? "var(--red-900)" : "var(--gray-300)",
                },
                "&.Mui-focused fieldset": {
                  border: "1px solid",
                  borderColor: showError
                    ? "var(--red-900)"
                    : "var(--brand-600) !important",
                  boxShadow: showError ? "none" : "0 0 0 3px var(--brand-200)",
                },
                "& input": {
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  lineHeight: "1.5",
                  "&::placeholder": {
                    opacity: 1,
                    color: "var(--gray-500)",
                  },
                },
              },
              "& .MuiFormHelperText-root": {
                color: showError ? "var(--red-900)" : "inherit",
                fontWeight: 400,
                marginLeft: 0,
              },
              ...sx,
            }}
          />
        )}
      />
    );
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={label ? 1 : 0}
      sx={{ ...sx, width: width ? `${width} !important` : "100% !important" }}
    >
      <FormLabel
        label={label}
        gap={gap}
        required={required}
        disabled={disabled}
      />

      {/* Wrap field + afterText */}
      {/* <Box display="flex"
       alignItems="center"
        gap={1} 
        width="100%"
        > */}
      {/* <Box flex={1} > */}
      {(Array.isArray(options) && options.length > 0) ||
      (options && options.url) ? (
        renderWithAutocomplete()
      ) : (
        <NoSpinnerTextField
          name={name}
          disabled={disabled}
          size="small"
          placeholder={placeholder}
          value={
            jsonValue !== undefined && jsonValue !== null && jsonValue !== ""
              ? jsonValue
              : (field?.defaultValue ?? "")
          }
          onChange={handleChange}
          onBlur={handleBlur}
          type={inputType}
          error={showError}
          helperText={
            showError ? helperText || `${label || "Field"} is required` : ""
          }
          InputProps={{
            startAdornment: startIconName && (
              <InputAdornment position="start">
                {typeof startIconName === "string"
                  ? renderIcon(startIconName)
                  : React.createElement(startIconName, {
                      color: iconColor || "var(--gray-400)",
                      size: 20,
                      fontWeight: "bold",
                    })}
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {!value && icon && (
                  <InputAdornment position="end">
                    <Box
                      onClick={disabled ? undefined : iconOnClick}
                      sx={{
                        cursor: disabled ? "not-allowed" : "pointer",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {typeof icon === "string"
                        ? renderIcon(icon)
                        : React.createElement(icon, {
                            color: iconColor || "var(--gray-400)",
                            size: 20,
                            fontWeight: "bold",
                          })}
                    </Box>
                  </InputAdornment>
                )}
                {endIconName && (
                  <InputAdornment position="end">
                    <SvgIcon
                      component={renderIcon(endIconName)}
                      inheritViewBox
                      sx={{ color: "gray", fontSize: 20 }}
                    />
                  </InputAdornment>
                )}
                {isPassword && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      size="small"
                    >
                      {showPassword
                        ? React.createElement(VisibilityOutlinedIcon)
                        : React.createElement(VisibilityOffOutlinedIcon)}
                    </IconButton>
                  </InputAdornment>
                )}
                {/* OTP icon always visible */}
                {isOTPField && (
                  <InputAdornment position="end">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "default",
                      }}
                    >
                      <BsCheckAll
                        color={
                          value && value.toString().length === 6
                            ? "green"
                            : "gray"
                        }
                        size={25}
                      />
                    </Box>
                  </InputAdornment>
                )}
              </>
            ),
            inputProps: {
              ...(inputMode ? { inputMode } : {}),
            },
          }}
          sx={{
            flex: 1, // take available space
            "& .MuiOutlinedInput-root": {
              fontSize: "16px",
              height: height,
              backgroundColor: disabled ? "var(--gray-50)" : "var(--gray-0)",
              cursor: disabled ? "not-allowed" : "text",
              "& fieldset": {
                border: showError
                  ? "1px solid var(--red-900) !important"
                  : "1px solid var(--gray-300)",
                borderRadius: 2,
              },
              "&:hover fieldset": {
                borderColor: showError ? "var(--red-900)" : "var(--gray-300)",
              },
              "&.Mui-focused fieldset": {
                border: "1px solid",
                borderColor: showError
                  ? "var(--red-900) !important"
                  : "var(--brand-600) !important",
                boxShadow: showError ? "none" : "0 0 0 3px var(--brand-200)",
              },
              "& input": {
                ...(isPassword ? { WebkitTextSecurity: "asterisk" } : {}),
                display: "flex",
                alignItems: "center",
                height: "100%",
                lineHeight: "1.5",
                "&::placeholder": {
                  opacity: 1,
                  color: "var(--gray-500)",
                  transform:
                    placeholder === "***********"
                      ? "translateY(3px)"
                      : "translateY(0px)",
                },
              },
            },
            "& .MuiFormHelperText-root": {
              color: showError ? "var(--red-900) !important" : "inherit",
              fontWeight: 400,
              marginLeft: 0,
            },
            ...sx,
          }}
        />
      )}
      {/* </Box> */}

      {/* afterText aligned to input baseline */}
      {afterText && (
        <Typography
          sx={{
            lineHeight: `${height}px`,
          }}
        >
          {afterText}
        </Typography>
      )}
    </Box>
    // </Box>
  );
};

export default React.memo(InputTextField);
